/**
 * A lightweight vanilla JS floating popup utility for TipTap suggestions.
 * Replaces tippy.js with minimal positioning logic.
 */

const POPUP_OFFSET = 8;

/**
 * Calculates the position for the popup based on placement and reference rect.
 * @param {DOMRect} referenceRect - The reference element's bounding rect
 * @param {HTMLElement} popup - The popup element
 * @param {string} placement - Placement option (e.g., 'top-start', 'bottom-start')
 * @returns {{ top: number, left: number }}
 */
const calculatePosition = (referenceRect, popup, placement) => {
    const popupRect = popup.getBoundingClientRect();
    const { scrollX, scrollY } = window;
    const { innerHeight, innerWidth } = window;

    let top = 0;
    let left = 0;

    const [vertical, horizontal] = placement.split('-');

    // Vertical positioning
    if (vertical === 'top') {
        top = referenceRect.top + scrollY - popupRect.height - POPUP_OFFSET;
    } else if (vertical === 'bottom') {
        top = referenceRect.bottom + scrollY + POPUP_OFFSET;
    }

    // Horizontal positioning
    if (horizontal === 'start') {
        left = referenceRect.left + scrollX;
    } else if (horizontal === 'end') {
        left = referenceRect.right + scrollX - popupRect.width;
    } else {
        // Center alignment
        left = referenceRect.left + scrollX + (referenceRect.width - popupRect.width) / 2;
    }

    // Boundary checks - keep popup within viewport
    // Check vertical overflow
    if (top < scrollY) {
        // Flip to bottom if not enough space on top
        top = referenceRect.bottom + scrollY + POPUP_OFFSET;
    } else if (top + popupRect.height > scrollY + innerHeight) {
        // Flip to top if not enough space on bottom
        top = referenceRect.top + scrollY - popupRect.height - POPUP_OFFSET;
    }

    // Check horizontal overflow
    if (left < scrollX) {
        left = scrollX + POPUP_OFFSET;
    } else if (left + popupRect.width > scrollX + innerWidth) {
        left = scrollX + innerWidth - popupRect.width - POPUP_OFFSET;
    }

    return { top, left };
};

/**
 * Creates a floating popup instance.
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.getReferenceClientRect - Function returning the reference element's rect
 * @param {HTMLElement} options.content - The popup content element
 * @param {string} [options.placement='top-start'] - Popup placement
 * @returns {Object} Popup instance with control methods
 */
const createFloatingPopup = ({ getReferenceClientRect, content, placement = 'top-start' }) => {
    // Store reference rect function for content change updates
    let currentGetReferenceClientRect = getReferenceClientRect;

    // Create the popup container
    const popup = document.createElement('div');
    popup.className = 'ssm-floating-popup';
    popup.style.cssText = `
        position: absolute;
        z-index: 9999;
        visibility: hidden;
    `;

    // Append content and add to DOM
    popup.appendChild(content);
    document.body.appendChild(popup);

    /**
     * Updates the popup position based on current reference rect.
     */
    const updatePosition = () => {
        const referenceRect = currentGetReferenceClientRect?.();
        if (!referenceRect) return;

        const { top, left } = calculatePosition(referenceRect, popup, placement);
        popup.style.top = `${top}px`;
        popup.style.left = `${left}px`;
        popup.style.visibility = 'visible';
    };

    // Watch for content changes to recalculate position
    // Only observe childList and subtree changes that affect layout/size
    // Attribute and characterData changes rarely affect positioning and cause excessive recalculations
    const observer = new MutationObserver(() => {
        // Use requestAnimationFrame to batch DOM reads after mutations
        requestAnimationFrame(updatePosition);
    });

    observer.observe(popup, {
        childList: true,
        subtree: true,
    });

    // Initial positioning
    updatePosition();

    return {
        /**
         * Updates popup properties.
         * @param {Object} props - New properties
         * @param {Function} [props.getReferenceClientRect] - New reference rect function
         */
        setProps: (props) => {
            if (props.getReferenceClientRect) {
                currentGetReferenceClientRect = props.getReferenceClientRect;
                updatePosition();
            }
        },

        /**
         * Hides the popup without destroying it.
         */
        hide: () => {
            popup.style.visibility = 'hidden';
        },

        /**
         * Shows the popup.
         */
        show: () => {
            updatePosition();
            popup.style.visibility = 'visible';
        },

        /**
         * Removes the popup from the DOM and cleans up observer.
         */
        destroy: () => {
            observer.disconnect();
            popup.remove();
        },

        /**
         * Returns the popup element.
         */
        element: popup,
    };
};

export default createFloatingPopup;
